/**
 * Tayangan: a sales live without going live. The seller plays a recorded video
 * (uploaded to R2) or a YouTube video, and every viewer sees the same moment of
 * it, as they would a live: nobody seeks, and someone arriving late lands where
 * everyone else is. The chat, the bag and the sales stay live as ever.
 *
 * Nothing on the server runs the clock. The room keeps when second 0 was, and
 * each player works its position out from server time.
 */
import type { LiveReplayConfig, LiveReplayPlay } from "./types";

/** Parts of an upload, one request each: well under the Worker's body limit,
 *  and above R2's 5 MB minimum for every part but the last. */
export const REPLAY_PART_BYTES = 20 * 1024 * 1024;
export const REPLAY_MAX_BYTES = 2 * 1024 * 1024 * 1024;
export const REPLAY_MAX_PARTS = Math.ceil(REPLAY_MAX_BYTES / REPLAY_PART_BYTES);

/** What can be uploaded, and the extension it is stored under. */
export const REPLAY_TYPES: Record<string, string> = {
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
};

export const DEFAULT_REPLAY_CONFIG: LiveReplayConfig = { source: "camera", loop: false, upload: null, youtube: null };

const FILE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(mp4|webm|mov)$/;

export function replayFileOk(file: string): boolean {
  return FILE.test(file);
}

/** The R2 key of a store's uploaded video. The store id comes from the
 *  server, never from the request, so one store cannot name another's file. */
export function replayKey(storeId: string, file: string): string {
  return `live-replay/${storeId}/${file}`;
}

const YT_ID = /^[A-Za-z0-9_-]{11}$/;

/** The video id in anything a seller might paste: a watch, youtu.be, shorts,
 *  embed or (finished) live link, or the bare id. Null when there is none. */
export function youtubeVideoId(input: string): string | null {
  const s = input.trim();
  if (YT_ID.test(s)) return s;
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase().replace(/^(www|m|music)\./, "");
  let id: string | null = null;
  if (host === "youtu.be") id = u.pathname.split("/")[1] ?? null;
  else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    id = u.pathname === "/watch" ? u.searchParams.get("v") : (u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/]+)/)?.[1] ?? null);
  }
  return id && YT_ID.test(id) ? id : null;
}

/** Where every player should be at `nowMs` (server time). Without a known
 *  duration the video simply runs on; the player's own end decides. */
export function replayPosition(
  play: Pick<LiveReplayPlay, "startedAt" | "loop">,
  nowMs: number,
  durationSec: number | null,
): { at: number; ended: boolean } {
  const elapsed = Math.max(0, (nowMs - play.startedAt) / 1000);
  if (!durationSec || !Number.isFinite(durationSec) || durationSec <= 0) return { at: elapsed, ended: false };
  if (play.loop) return { at: elapsed % durationSec, ended: false };
  return elapsed >= durationSec ? { at: durationSec, ended: true } : { at: elapsed, ended: false };
}

/** Ulang auto switched while playing. Off: the current round plays to its end,
 *  not cut short by the rounds before it. On after the end: from the start. */
export function withLoop(play: LiveReplayPlay, loop: boolean, nowMs: number): LiveReplayPlay {
  if (play.loop === loop) return play;
  if (!play.durationSec) return { ...play, loop };
  const pos = replayPosition(play, nowMs, play.durationSec);
  return { ...play, loop, startedAt: pos.ended ? nowMs : nowMs - Math.round(pos.at * 1000) };
}

/** Can this source be played right now? The camera is the seller's device's to say. */
export function replayReady(config: LiveReplayConfig): boolean {
  if (config.source === "upload") return Boolean(config.upload);
  if (config.source === "youtube") return Boolean(config.youtube);
  return false;
}

/** The YouTube player for a Tayangan: no controls, no keyboard, no related
 *  videos from other channels, muted until the viewer turns the sound on (the
 *  only way a browser autoplays), and driven over postMessage (enablejsapi). */
export function youtubeReplaySrc(id: string, startSec: number, loop: boolean, origin: string): string {
  const p = new URLSearchParams({
    enablejsapi: "1",
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    controls: "0",
    disablekb: "1",
    fs: "0",
    rel: "0",
    iv_load_policy: "3",
    modestbranding: "1",
    start: String(Math.max(0, Math.floor(startSec))),
    origin,
  });
  if (loop) {
    p.set("loop", "1");
    p.set("playlist", id);
  }
  return `https://www.youtube-nocookie.com/embed/${id}?${p}`;
}

/** 1:05:09 or 4:07 — for the seller's "12:30 / 45:00". */
export function clockText(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
